import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  TextField,
  Tooltip,
  Chip,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios, { getData } from "../../../../axios";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import { loadEquipment } from "../../../../ducks/data/equipment";
import { selectEquipment } from "../../../../ducks/data/equipment/selectors";
import { loadEquipmentTypes } from "../../../../ducks/data/equipment-types";
import { selectEquipmentTypes } from "../../../../ducks/data/equipment-types/selectors";
import { loadEquipmentDepots } from "../../../../ducks/data/equipment-depots";
import { selectEquipmentDepots } from "../../../../ducks/data/equipment-depots/selectors";
import {
  cargoEquipmentLinksT,
  CargoEquipmentLink,
  CargoEquipmentRole,
  EquipmentStatus,
} from "dora-contracts";
import { decode } from "io-ts-promise";
import { isNotNull } from "dora-shared";
import keyBy from "lodash/keyBy";
import { notify } from "../../../../ducks/notifications";
import { loadRental } from "../../../../ducks/data/rentals";
import { selectRentalsById } from "../../../../ducks/data/rentals/selectors";
import { DoraNumberInput } from "dora-shared/frontend";
import {
  draftEquipmentCleared,
  draftEquipmentQuantitySet,
  draftEquipmentSet,
} from "../../../../ducks/data/draft-cargo-equipment";
import { selectDraftCargoEquipment } from "../../../../ducks/data/draft-cargo-equipment/selectors";

const decodeLinks = decode(cargoEquipmentLinksT);

type Option = { id: string; label: string; status: EquipmentStatus };

// One unit on the order: a saved link, or a pick on an order not created yet.
type Row = {
  key: string;
  equipmentId: string;
  role: CargoEquipmentRole;
  quantity: number;
  rentalId: string | null;
};

// `<input type="number">` hands back a string. Empty input - and anything the
// browser could not parse as a number - means "nothing entered", which is
// modelled as null rather than as 0 or "".
const parseQuantity = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

// Match the driver-absence styling in the "add driver" dropdown: a light red
// row background when occupied/unavailable (at a customer, in service, blocked)
// and a light amber background when reserved/planned. Undefined = available.
const bgForStatus = (
  s: EquipmentStatus
): { base: string; hover: string } | undefined => {
  switch (s) {
    case "WITH_CUSTOMER":
    case "UNDER_SERVICE":
    case "UNAVAILABLE":
      return { base: "var(--error-200)", hover: "var(--error-300)" };
    case "PLANNED":
    case "IN_TRANSIT":
      return { base: "var(--warning-100)", hover: "var(--warning-200)" };
    default:
      return undefined;
  }
};

/**
 * Equipment picker shown next to "Varer" in the order dialog. On a saved
 * cargo it attaches/detaches equipment via the cargo-equipment API. On a cargo
 * not created yet (no cargoId) the picks are kept in the draft-cargo-equipment
 * duck and sent with the create request, like the draft invoice lines. The
 * role is derived from the unit's current status: a unit that is
 * WITH_CUSTOMER is attached as PICKUP (bring it home), anything else as
 * DELIVER. The server resolves rentalId from the unit's active rental, and a
 * DELIVER on an order with a customer starts a rental when the order is
 * saved; a tooltip on the chip names it. Un-numbered (bulk) equipment gets a
 * quantity field. Everything except SOLD units is selectable, colour-coded by
 * status.
 */

const roleForStatus = (s: EquipmentStatus): CargoEquipmentRole =>
  s === "WITH_CUSTOMER" ? "PICKUP" : "DELIVER";
const EquipmentSelect = ({ cargoId }: { cargoId?: string | null }) => {
  const { t } = useTranslation(["equipment"]);
  const dispatch = useAppDispatch();
  const equipment = useSelector(selectEquipment);
  const types = useSelector(selectEquipmentTypes);
  const typeById = useMemo(() => keyBy(types, "id"), [types]);
  const equipmentById = useMemo(() => keyBy(equipment, "id"), [equipment]);
  const depots = useSelector(selectEquipmentDepots);
  const depotsById = useMemo(() => keyBy(depots, "id"), [depots]);
  const [links, setLinks] = useState<CargoEquipmentLink[]>([]);
  const draftPicks = useSelector(selectDraftCargoEquipment);
  const isDraft = !cargoId;
  const [reqInProgress, setReqInProgress] = useState(false);
  // The links carry `rentalId` but not the rental itself, so the number shown
  // on a chip comes from the rentals duck, which `loadRental` fills in below.
  const rentalsById = useSelector(selectRentalsById);
  // Bulk quantity inputs, keyed by row key. Controlled so an empty/invalid
  // entry can be rejected with a hint instead of silently turning into 1;
  // null is "the field is empty", which is never a quantity we send.
  const [quantityDrafts, setQuantityDrafts] = useState<
    Record<string, number | null>
  >({});
  const [quantityErrors, setQuantityErrors] = useState<Record<string, boolean>>(
    {}
  );

  // Functional updates so two rows edited in quick succession merge instead
  // of overwriting each other.
  const setQuantityDraft = (key: string, quantity: number | null) =>
    setQuantityDrafts((drafts) => ({ ...drafts, [key]: quantity }));
  const setQuantityError = (key: string, hasError: boolean) =>
    setQuantityErrors((errors) => ({ ...errors, [key]: hasError }));

  const reload = useCallback(async () => {
    if (!cargoId) {
      setLinks([]);
      return;
    }
    const res = await axios.get(`/api/cargo-equipment/cargo/${cargoId}`);
    const next = await decodeLinks(getData(res));
    setLinks(next);
    setQuantityDrafts(Object.fromEntries(next.map((l) => [l.id, l.quantity])));
    setQuantityErrors({});
    await Promise.all(
      Array.from(new Set(next.map((l) => l.rentalId).filter(isNotNull))).map(
        (rentalId) => dispatch(loadRental(rentalId))
      )
    );
  }, [cargoId, dispatch]);

  useEffect(() => {
    dispatch(loadEquipment());
    dispatch(loadEquipmentTypes());
    dispatch(loadEquipmentDepots());
    reload().catch(() => {
      dispatch(
        notify({ message: t("equipment:cargoCard.loadError"), type: "error" })
      );
    });
  }, [dispatch, reload, t]);

  // A new order starts with no equipment, whatever an earlier abandoned
  // create dialog left in the draft.
  useEffect(() => {
    if (isDraft) {
      dispatch(draftEquipmentCleared());
      setQuantityDrafts({});
      setQuantityErrors({});
    }
  }, [dispatch, isDraft]);

  const rows: Row[] = useMemo(
    () =>
      isDraft
        ? draftPicks.map((p) => ({
            key: p.equipmentId,
            equipmentId: p.equipmentId,
            role: p.role,
            quantity: p.quantity,
            rentalId: null,
          }))
        : links.map((l) => ({
            key: l.id,
            equipmentId: l.equipmentId,
            role: l.role,
            quantity: l.quantity,
            rentalId: l.rentalId,
          })),
    [isDraft, draftPicks, links]
  );

  const labelFor = useCallback(
    (id: string): string => {
      const e = equipmentById[id];
      if (!e) return "?";
      const typeName = e.equipmentTypeId
        ? typeById[e.equipmentTypeId]?.name
        : null;
      return e.number || typeName || t("equipment:units.noNumber");
    },
    [equipmentById, typeById, t]
  );

  const isBulk = useCallback(
    (id: string): boolean => {
      const e = equipmentById[id];
      if (!e) return false;
      if (e.equipmentTypeId && typeById[e.equipmentTypeId]) {
        return typeById[e.equipmentTypeId].isNumbered === false;
      }
      return !e.number;
    },
    [equipmentById, typeById]
  );

  const linkedIds = useMemo(
    () => new Set(rows.map((r) => r.equipmentId)),
    [rows]
  );
  const rowByEquipmentId = useMemo(() => keyBy(rows, "equipmentId"), [rows]);

  // Show all equipment (except retired/sold), colour-coded by status so the
  // dispatcher can see occupied/reserved units too. Available first.
  const options: Option[] = useMemo(
    () =>
      equipment
        .filter((e) => e.status !== "SOLD" || linkedIds.has(e.id))
        .map((e) => ({ id: e.id, label: labelFor(e.id), status: e.status }))
        .sort((a, b) => {
          const av = a.status === "AVAILABLE" ? 0 : 1;
          const bv = b.status === "AVAILABLE" ? 0 : 1;
          return av - bv || a.label.localeCompare(b.label);
        }),
    [equipment, linkedIds, labelFor]
  );

  // Where a free unit stands: its depot, else the home typed on the unit. A
  // unit that is out is somewhere else, so it gets no place here.
  const locationFor = (o: Option): string | null => {
    if (o.status !== "AVAILABLE") return null;
    const e = equipmentById[o.id];
    if (!e) return null;
    return (e.depotId && depotsById[e.depotId]?.name) || e.homeLocation || null;
  };

  const selected = useMemo(
    () => options.filter((o) => linkedIds.has(o.id)),
    [options, linkedIds]
  );

  const notifyUpdateError = () =>
    dispatch(
      notify({ message: t("equipment:cargoCard.updateError"), type: "error" })
    );

  const onDraftChange = (next: Option[]) => {
    const pickById = keyBy(draftPicks, "equipmentId");
    dispatch(
      draftEquipmentSet(
        next.map(
          (o) =>
            pickById[o.id] ?? {
              equipmentId: o.id,
              role: roleForStatus(o.status),
              quantity: 1,
              note: null,
              rentalId: null,
            }
        )
      )
    );
    setQuantityDrafts((drafts) => ({
      ...Object.fromEntries(next.map((o) => [o.id, 1])),
      ...drafts,
    }));
  };

  const onChange = async (next: Option[]) => {
    if (!cargoId) {
      onDraftChange(next);
      return;
    }
    if (reqInProgress) return;
    setReqInProgress(true);
    try {
      const nextIds = new Set(next.map((o) => o.id));
      for (const o of next) {
        if (!linkedIds.has(o.id)) {
          await axios.post(`/api/cargo-equipment/cargo/${cargoId}`, {
            equipmentId: o.id,
            role: roleForStatus(o.status),
            quantity: 1,
            note: null,
            // Resolved server-side from the unit's active rental.
            rentalId: null,
          });
        }
      }
      for (const l of links) {
        if (!nextIds.has(l.equipmentId)) {
          await axios.delete(`/api/cargo-equipment/${l.id}`);
        }
      }
    } catch {
      notifyUpdateError();
    } finally {
      // The attach/detach loop is sequential, so a failure halfway leaves
      // some links changed. Always re-read so the picker shows the real
      // server state rather than the optimistic selection.
      try {
        await reload();
      } catch {
        notifyUpdateError();
      }
      setReqInProgress(false);
    }
  };

  const commitQuantity = async (row: Row) => {
    if (reqInProgress) return;
    const quantity = quantityDrafts[row.key] ?? null;
    if (quantity === null || !Number.isInteger(quantity) || quantity < 1) {
      // Deliberately not defaulted to 1: put the last saved value back and
      // flag the field, so a mistyped quantity is visible rather than
      // silently becoming a quantity nobody asked for.
      setQuantityDraft(row.key, row.quantity);
      setQuantityError(row.key, true);
      return;
    }
    setQuantityError(row.key, false);
    if (quantity === row.quantity) return;
    if (isDraft) {
      dispatch(
        draftEquipmentQuantitySet({ equipmentId: row.equipmentId, quantity })
      );
      return;
    }
    setReqInProgress(true);
    try {
      await axios.patch(`/api/cargo-equipment/${row.key}`, { quantity });
      await reload();
    } catch {
      notifyUpdateError();
      setQuantityDraft(row.key, row.quantity);
    } finally {
      setReqInProgress(false);
    }
  };

  const bulkRows = rows.filter((r) => isBulk(r.equipmentId));

  const control = (
    <Autocomplete
      multiple
      fullWidth
      size="small"
      disabled={reqInProgress}
      slotProps={{
        popper: {
          style: { width: "fit-content" },
          modifiers: [{ name: "flip", enabled: false }],
        },
        paper: { sx: { minWidth: 220 } },
      }}
      options={options}
      value={selected}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_e, next) => onChange(next as Option[])}
      renderTags={(value, getTagProps) =>
        value.map((o, i) => {
          const bg = bgForStatus(o.status);
          const row = rowByEquipmentId[o.id];
          const role = row?.role;
          const rentalId = row?.rentalId;
          const rentalNumber: string | null = rentalId
            ? rentalsById[rentalId]?.rentalNumber ?? null
            : null;
          const chip = (
            <Chip
              {...getTagProps({ index: i })}
              key={o.id}
              size="small"
              label={
                role
                  ? `${o.label} ${t(`equipment:cargoCard.roleSuffix.${role}`)}`
                  : o.label
              }
              // The role is part of the label, so the chip is never cut
              // short; the field grows with its chips instead.
              sx={{ maxWidth: "none", ...(bg && { backgroundColor: bg.base }) }}
            />
          );
          if (!rentalId) return chip;
          return (
            <Tooltip
              key={o.id}
              title={
                rentalNumber
                  ? t("equipment:cargoCard.rentalTooltip", {
                      number: rentalNumber,
                    })
                  : t("equipment:cargoCard.rentalTooltipNoNumber")
              }
            >
              {chip}
            </Tooltip>
          );
        })
      }
      renderOption={(props, o) => {
        const bg = bgForStatus(o.status);
        return (
          <Box
            component="li"
            {...props}
            key={o.id}
            sx={{
              whiteSpace: "nowrap",
              ...(bg && {
                backgroundColor: `${bg.base} !important`,
                "&:hover, &.Mui-focused": {
                  backgroundColor: `${bg.hover} !important`,
                },
              }),
            }}
          >
            {o.label}
            <span style={{ marginLeft: 6, color: "var(--gray-600)" }}>
              {[t(`equipment:statuses.${o.status}`), locationFor(o)]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={t("equipment:units.listHeading")} />
      )}
    />
  );

  return (
    <div>
      {control}
      {bulkRows.length > 0 && (
        <Stack spacing={0.5} mt={1}>
          {bulkRows.map((r) => (
            <Stack key={r.key} direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ minWidth: 90 }}>
                {labelFor(r.equipmentId)}
              </Typography>
              <Box sx={{ width: 110 }}>
                <DoraNumberInput
                  aria-label={t("equipment:units.fields.quantity")}
                  value={quantityDrafts[r.key] ?? ""}
                  min={1}
                  step={1}
                  onChange={(value: string) =>
                    setQuantityDraft(r.key, parseQuantity(value))
                  }
                  onBlur={() => commitQuantity(r)}
                  error={!!quantityErrors[r.key]}
                  disabled={reqInProgress}
                />
              </Box>
              {quantityErrors[r.key] && (
                <Typography variant="caption" color="error">
                  {t("equipment:cargoCard.quantityMin")}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </div>
  );
};

export default EquipmentSelect;
